import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useModalStore } from "../../lib/store/useModalStore";
import { tournamentApplyPayInfo } from "../../@apis/v1/payment.service";
import Button from "../../components/atoms/button";
import FailScreen from "../../components/layout/reservation/FailScreen";

// 라벨/값 한 줄
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-[8px] items-start">
      <p className="min-w-[80px] shrink-0 text-[#8e8e93]">{label}</p>
      <div className="flex-1 min-w-0 text-black break-keep whitespace-pre-line [overflow-wrap:anywhere]">
        {value}
      </div>
    </div>
  );
}

/**
 * 무통장입금 접수 완료 화면.
 * 온라인 결제와 동일하게 페이지로 노출하며, 접수 번호만으로 내용을 다시 조회해
 * 새로고침이나 재진입에도 같은 화면이 유지된다.
 */
export default function TournamentApplyCompletePage() {
  const { applyId } = useParams();
  const navigate = useNavigate();
  const { openModal } = useModalStore();

  // 접수 직후 화면이라 뒤로 가면 입력 단계로 되돌아가므로 홈 이동으로 안내한다.
  React.useEffect(() => {
    const original = window.closeDialogIfOpened;
    window.closeDialogIfOpened = () => {
      const modals = useModalStore.getState().modals;
      if (modals.length > 0) {
        useModalStore.getState().closeModal(modals[modals.length - 1].id);
        return true;
      }
      openModal("CONFIRM_DIALOG", {
        title: "홈으로 이동",
        content:
          "접수 후에는 뒤로 가실 수 없습니다.\n홈 화면으로 이동하시겠습니까?",
        onClick: () => navigate("/", { replace: true }),
      });
      return true;
    };
    return () => {
      window.closeDialogIfOpened = original;
    };
  }, [navigate, openModal]);

  const {
    data: applyInfo,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["tournament-apply-pay-info", applyId],
    queryFn: () => tournamentApplyPayInfo(applyId as string),
    enabled: !!applyId && applyId !== "null",
  });
  const applyData = applyInfo?.data;

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-[100dvh] items-center justify-center text-[#8e8e93]">
        접수 정보를 불러오는 중입니다...
      </div>
    );
  }

  if (isError || !applyData) {
    return (
      <FailScreen message="접수 정보를 불러오지 못했습니다.\n잠시 후 다시 시도해 주세요." />
    );
  }

  const isPaymentWait = applyData.myStampLabel === "결제대기";
  const subtitle = isPaymentWait
    ? "접수내역에서 입금계좌를 확인 후 입금해 주세요."
    : "관리자 확인 후 순차적으로 승인 처리됩니다.";

  return (
    <div className="flex flex-col min-h-[100dvh]">
      <div className="flex-1 bg-[#f2f2f7] flex flex-col items-center justify-center gap-[20px] p-[20px]">
        <div className="flex flex-col items-center gap-[12px] w-full">
          <div className="bg-[#ff3b30] size-12 rounded-full flex items-center justify-center">
            <img className="w-5 h-3.5" src="/icons/ic-check.webp" alt="완료" />
          </div>
          <div className="flex flex-col items-center gap-[8px] text-center w-full">
            <p className="text-3xl font-medium text-black">신청 완료</p>
            <p className="text-base font-semibold text-[#8e8e93] break-keep whitespace-pre-line">
              {subtitle}
            </p>
          </div>
        </div>

        <div className="w-full bg-white border border-[#e5e5ea] rounded-[10px] px-[16px] py-[20px]">
          <div className="flex flex-col gap-[8px] text-sm font-medium">
            <InfoRow label="대회명" value={applyData.tournamentName || "-"} />
            <InfoRow label="대회날짜" value={applyData.calendarDate || "-"} />
            <InfoRow label="대회일정" value={applyData.timeType || "-"} />
            <InfoRow
              label="결제상태"
              value={
                <span className="inline-flex items-center justify-center rounded-[5px] bg-[#fff4d8] px-[10px] py-[2.5px] text-xs font-medium text-[#a2845e]">
                  {applyData.myStampLabel || "-"}
                </span>
              }
            />
            <InfoRow label="결제수단" value={applyData.payMethodLabel || "-"} />
          </div>
        </div>
      </div>

      <div className="p-[12px] flex gap-[10px] bg-white pb-[calc(12px+var(--safe-area-inset-bottom))]">
        <Button
          className="flex-1 font-normal"
          variant="white"
          label="홈으로"
          onClick={() => navigate("/", { replace: true })}
        />
        <Button
          className="flex-1 font-normal whitespace-pre-line break-keep"
          variant="black"
          label="접수내역 보기"
          onClick={() =>
            navigate(
              `/setting/tournament-history/${applyData.tournamentId}/${applyData.applyId}`,
            )
          }
        />
      </div>
    </div>
  );
}
